import { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from './Card';
import { Chip } from './Chip';
import { api, type Supplement } from '../lib/api';
import { AddSupplementModal } from './AddSupplementModal';
import { SupplementRow } from './SupplementRow';
import { TimePicker } from './TimePicker';

// SupplementsCard fetches and renders the user's supplement list with
// per-row take/skip controls, an info ("?") expander that hits the coach
// proxy, an edit drawer, and an "Add" entry point at the bottom.
//
// "Set times" flips the card into selection mode: each row grows a checkbox
// and a footer bar applies one shared time to everything ticked. That write
// REPLACES each selected supplement's schedule — aligning several supplements
// onto one minute is the point (they then coalesce into a single push), so the
// bar says so before the user commits.
//
// On first load (or after every Add/Delete/bulk apply) we re-fetch the whole
// list so the UI stays a pure projection of server state — no optimistic
// merging.
export function SupplementsCard() {
  const [items, setItems] = useState<Supplement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const [selecting, setSelecting] = useState(false);
  const [selectedIDs, setSelectedIDs] = useState<number[]>([]);
  const [bulkHour, setBulkHour] = useState(9);
  const [bulkMinute, setBulkMinute] = useState(0);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  // Synchronous double-submit guard — setState lags a render, so `disabled`
  // alone still lets a second tap within ~16ms fire a duplicate write.
  const bulkBusyRef = useRef(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const list = await api.getSupplements();
      setItems(list);
    } catch (e) {
      setError(String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Drop any selected id that no longer exists — a row can be deleted from its
  // own "⋯" menu while selection mode is open, and a stale id would make the
  // whole bulk write 404 (it is all-or-nothing on the server).
  useEffect(() => {
    if (!items) return;
    setSelectedIDs((prev) => {
      const live = prev.filter((id) => items.some((s) => s.id === id));
      return live.length === prev.length ? prev : live;
    });
  }, [items]);

  const exitSelection = () => {
    setSelecting(false);
    setSelectedIDs([]);
    setBulkError(null);
  };

  const toggleSelected = (id: number, next: boolean) =>
    setSelectedIDs((prev) => (next ? [...prev, id] : prev.filter((x) => x !== id)));

  const applyBulkTime = async () => {
    if (bulkBusyRef.current) return;
    if (selectedIDs.length === 0) return;
    bulkBusyRef.current = true;
    setBulkBusy(true);
    setBulkError(null);
    try {
      await api.bulkSetSupplementTime({
        supplement_ids: selectedIDs,
        hour: bulkHour,
        minute: bulkMinute,
      });
      exitSelection();
      await refresh();
    } catch (e) {
      setBulkError(String(e));
    } finally {
      bulkBusyRef.current = false;
      setBulkBusy(false);
    }
  };

  return (
    <Card
      eyebrow="Supplements"
      hint="Edit doses, schedules, and skip individual reminders."
      action={
        <div className="flex items-center gap-2 shrink-0">
          {!selecting && items !== null && items.length > 0 && (
            <button
              type="button"
              onClick={() => setSelecting(true)}
              className="btn-secondary text-[11px] px-3.5 py-2 font-mono uppercase tracking-[0.14em]"
            >
              Set times
            </button>
          )}
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="btn-secondary text-[11px] px-3.5 py-2 font-mono uppercase tracking-[0.14em]"
          >
            + Add
          </button>
        </div>
      }
    >
      {error && (
        <pre className="text-xs text-danger whitespace-pre-wrap break-all">{error}</pre>
      )}
      {items === null && !error && (
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-faint py-4 text-center">
          Loading…
        </p>
      )}
      {items !== null && items.length === 0 && (
        <div className="space-y-2 py-2">
          <p className="text-sm text-fg-muted">
            No supplements yet. Tap <span className="font-medium text-fg">+ Add</span> to start.
          </p>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="space-y-2">
          {items.map((s) => (
            <SupplementRow
              key={s.id}
              supplement={s}
              onChanged={refresh}
              selectable={selecting}
              selected={selectedIDs.includes(s.id)}
              onSelectedChange={(next) => toggleSelected(s.id, next)}
            />
          ))}
        </div>
      )}

      {selecting && (
        <div
          role="group"
          aria-label="Set a shared time"
          className="rounded-panel border border-line bg-bg-raised-2 p-3 space-y-2.5"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="label-eyebrow">Shared time</span>
            <TimePicker
              hour={bulkHour}
              minute={bulkMinute}
              onChange={(h, m) => {
                setBulkHour(h);
                setBulkMinute(m);
              }}
              ariaLabel="Shared time"
            />
          </div>
          <p className="text-xs text-fg-muted leading-snug">
            Replaces existing times for the selected supplements.
          </p>
          {bulkError && (
            <pre role="alert" className="text-xs text-danger whitespace-pre-wrap break-all">
              {bulkError}
            </pre>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={applyBulkTime}
              disabled={selectedIDs.length === 0 || bulkBusy}
              className="btn-primary flex-1 py-3 text-sm"
            >
              {bulkBusy ? 'Applying…' : `Apply to ${selectedIDs.length}`}
            </button>
            <button
              type="button"
              onClick={exitSelection}
              disabled={bulkBusy}
              className="btn-secondary flex-1 py-3 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="pt-4 mt-4 border-t border-line">
        <Chip tone="muted">Mobility flow lives in Training tab</Chip>
      </div>

      <AddSupplementModal
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={() => {
          setAdding(false);
          void refresh();
        }}
      />
    </Card>
  );
}
